import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAdminAuth } from "auth/AdminAuthProvider";
import {
  adminRequest,
  createResource,
  deleteResource,
  getNewsAutomationStatus,
  listAllResource,
  resourceAction,
  unwrapItems,
  updateResource,
  uploadMedia,
} from "services/adminApi";

const categories = [
  "أخبار السفر",
  "أخبار الطيران",
  "أخبار المطار",
  "تنبيهات السفر",
  "نصائح السفر",
  "خدمات المطار",
  "أخبار المعابر",
  "جوازات وتأشيرات",
  "وجهات وخطوط جديدة",
  "وجهات",
  "دليل السفر",
];

const impactLevels = [
  ["low", "🟢 تأثير منخفض", "success"],
  ["attention", "🟡 يستحق الانتباه", "warning"],
  ["may_affect", "🟠 قد يؤثر على رحلتك", "warning"],
  ["direct", "🔴 تأثير مباشر على السفر", "error"],
];

const articleActionButtonSx = {
  neutral: {
    backgroundColor: "#FFFFFF",
    borderColor: "#9CAFB7",
    color: "#12313F",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#F2FAFC", borderColor: "#12313F" },
  },
  review: {
    backgroundColor: "#FFFFFF",
    borderColor: "#B45309",
    color: "#92400E",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#FFF7ED", borderColor: "#92400E" },
  },
  primary: {
    backgroundColor: "#13715F",
    color: "#FFFFFF",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#0F5B4D" },
    "&.Mui-disabled": { backgroundColor: "#CED4DA", color: "#495057" },
  },
  emphasis: {
    backgroundColor: "#12313F",
    color: "#FFFFFF",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#0A2632" },
    "&.Mui-disabled": { backgroundColor: "#CED4DA", color: "#495057" },
  },
  danger: {
    backgroundColor: "#FFFFFF",
    borderColor: "#B91C1C",
    color: "#991B1B",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#FEF2F2", borderColor: "#991B1B" },
  },
};

function impactLevelMeta(value) {
  return impactLevels.find(([id]) => id === value) || impactLevels[0];
}

const emptyArticle = {
  title: "",
  summary: "",
  contentHtml: "",
  category: "أخبار السفر",
  impactLevel: "low",
  isUrgent: false,
  locationLabel: "",
  coverImage: "",
  publishAt: "",
  showUnderServices: true,
  sortOrder: 100,
};

function toLocalDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 16);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function fromLocalDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

function isScheduled(item) {
  return item.status === "published" && item.publishAt && new Date(item.publishAt) > new Date();
}

function statusLabel(item) {
  if (isScheduled(item)) return "مجدول";
  return (
    {
      draft: "مسودة",
      review: "للمراجعة",
      approved: "معتمد",
      image_ready: "الصورة جاهزة",
      published: "منشور",
      archived: "مؤرشف",
    }[item.status] || "مسودة"
  );
}

function statusColor(item) {
  if (isScheduled(item)) return "info";
  return {
    draft: "warning",
    review: "info",
    approved: "info",
    image_ready: "success",
    published: "success",
    archived: "default",
  }[item.status];
}

const workflowSteps = ["مراجعة النص", "اعتماد النص", "تجهيز الصورة", "النشر"];

function articleTab(item) {
  if (item.status === "published") return "live";
  if (item.status === "archived") return "archived";
  return "draft";
}

function workflowCompletedSteps(item) {
  if (item.status === "published") return 4;
  if (item.status === "archived" && Number(item.publishedVersion || 0) > 0) return 4;
  if (item.status === "image_ready") return 3;
  if (item.status === "approved") return item.coverImage && !item.automation ? 3 : 2;
  if (item.status === "review") return 1;
  return 0;
}

function nextWorkflowAction(item, canPublish) {
  if (item.status === "published" || item.status === "archived") return null;
  if (!canPublish) {
    return item.status === "draft"
      ? { action: "review", label: "الخطوة التالية: إرسال للمراجعة" }
      : { label: "بانتظار اعتماد المدير", disabled: true };
  }
  if (["draft", "review"].includes(item.status)) {
    return { action: "approve", label: "الخطوة التالية: اعتماد النص" };
  }
  const imageReady =
    Boolean(item.coverImage) && (!item.automation || item.status === "image_ready");
  if (["approved", "image_ready"].includes(item.status) && !imageReady) {
    return { action: "generate-image", label: "الخطوة التالية: توليد الصورة" };
  }
  if (imageReady) return { action: "publish", label: "الخطوة التالية: نشر الخبر" };
  return null;
}

function formatArticleDate(value) {
  if (!value) return "غير متوفر";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ar", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function ArticleWorkflowSteps({ item }) {
  const completed = workflowCompletedSteps(item);
  return (
    <MDBox display="flex" gap={0.5} mt={0.75} flexWrap="wrap">
      {workflowSteps.map((label, index) => {
        const isComplete = index < completed;
        const isCurrent = index === completed && completed < workflowSteps.length;
        return (
          <Chip
            key={label}
            size="small"
            label={`${isComplete ? "✓" : index + 1} ${label}`}
            color={isComplete ? "success" : isCurrent ? "info" : "default"}
            variant={isComplete || isCurrent ? "filled" : "outlined"}
          />
        );
      })}
    </MDBox>
  );
}

ArticleWorkflowSteps.propTypes = { item: PropTypes.object.isRequired };

function safePreviewHtml(value) {
  const template = document.createElement("template");
  template.innerHTML = value || "<p>محتوى المقال</p>";
  template.content
    .querySelectorAll("script, iframe, object, embed, svg, math, style, link, meta")
    .forEach((element) => element.remove());
  template.content.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const valueText = attribute.value.trim().toLowerCase();
      if (
        name.startsWith("on") ||
        name === "style" ||
        ((name === "href" || name === "src") && !valueText.startsWith("https://"))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });
  return template.innerHTML;
}

function ArticlesPage() {
  const auth = useAdminAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTab, setSelectedTab] = useState("live");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState("");
  const [success, setSuccess] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fetchingNews, setFetchingNews] = useState(false);
  const [actionBusy, setActionBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    listAllResource("articles", { search })
      .then((payload) => setItems(unwrapItems(payload)))
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(load, 250);
    return () => clearTimeout(timeout);
  }, [load]);

  const tabCounts = useMemo(
    () =>
      items.reduce(
        (counts, item) => ({ ...counts, [articleTab(item)]: counts[articleTab(item)] + 1 }),
        { live: 0, draft: 0, archived: 0 }
      ),
    [items]
  );

  const orderedItems = useMemo(
    () =>
      items
        .filter((item) => articleTab(item) === selectedTab)
        .sort(
          (first, second) =>
            Number(first.sortOrder ?? 9999) - Number(second.sortOrder ?? 9999) ||
            String(second.publishAt || second.publishedAt || "").localeCompare(
              String(first.publishAt || first.publishedAt || "")
            )
        ),
    [items, selectedTab]
  );

  const openEditor = (item = null) => {
    const values = item?.draft || item?.effective || item || emptyArticle;
    setUploadInfo("");
    setEditing(item || {});
    setForm({ ...emptyArticle, ...values });
  };

  const closeEditor = () => {
    if (!saving) {
      setUploadInfo("");
      setEditing(null);
      setForm(emptyArticle);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const articleId = editing?.entityId || editing?.articleId;
      const values = {
        ...form,
        sortOrder: Number(form.sortOrder || 0),
        ...(editing?.version != null ? { expectedVersion: editing.version } : {}),
      };
      const saved = articleId
        ? await updateResource("articles", articleId, values)
        : await createResource("articles", values);
      setEditing(saved);
      setForm({ ...emptyArticle, ...(saved.draft || values) });
      if (!articleId) setSelectedTab("draft");
      load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const action = async (item, name) => {
    const articleId = item.entityId || item.articleId;
    const prompts = {
      publish: item.publishAt
        ? "هل تريد نشر المقال في الموعد المحدد؟ سيُرسل إشعار تلقائيًا بعد نجاح النشر."
        : "هل تريد نشر المقال الآن؟ سيُرسل إشعار تلقائيًا بعد نجاح النشر.",
      review: "هل تريد إرسال المقال للمراجعة؟",
      approve: "هل راجعت نص الخبر وتريد اعتماده لتوليد الصورة؟",
      "generate-image": item.coverImage
        ? "هل تريد توليد صورة جديدة من سياق الخبر واستبدال الصورة الحالية؟"
        : "هل تريد توليد صورة من سياق الخبر؟ قد تستغرق العملية عدة ثوانٍ.",
      notification: "هل تريد إرسال إشعار بهذا الخبر إلى المشتركين الآن؟ لا يمكن تكرار الإرسال.",
      "followers-notification":
        "هل تريد إرسال هذا التحديث إلى متابعي الخبر فقط؟ يمكن الإرسال مرة واحدة لكل نسخة منشورة.",
      archive: "هل تريد أرشفة المقال وإخفاءه من التطبيق؟",
      duplicate: "هل تريد إنشاء نسخة جديدة من المقال؟",
    };
    if (!articleId || !window.confirm(prompts[name])) return;
    setActionBusy(`${articleId}:${name}`);
    try {
      const result = await resourceAction("articles", articleId, name, {
        environment: process.env.REACT_APP_ENVIRONMENT || "dev",
        confirm: true,
      });
      const isFirstPublish = name === "publish" && Number(item.publishedVersion || 0) === 0;
      if (isFirstPublish) {
        try {
          const notification = await resourceAction("articles", articleId, "notification", {});
          setSuccess(`تم اعتماد المقال وإرسال الإشعار بنجاح (${notification.notificationId}).`);
        } catch (notificationError) {
          setError(`تم اعتماد المقال ونشره، لكن تعذر إرسال الإشعار: ${notificationError.message}`);
        }
      }
      if (name === "notification") {
        setSuccess(`تم إرسال الإشعار بنجاح (${result.notificationId}).`);
      }
      if (name === "followers-notification") {
        setSuccess(`تم إرسال التحديث إلى متابعي الخبر (${result.notificationId}).`);
      }
      if (name === "approve") {
        setSuccess("تم اعتماد نص الخبر. يمكنك الآن توليد الصورة من السياق.");
      }
      if (name === "generate-image") {
        setSuccess("تم توليد صورة الخبر وحفظها. راجعها ثم انشر المقال.");
      }
      load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setActionBusy("");
    }
  };

  const sendToSocial = async (item) => {
    const articleId = item.entityId || item.articleId;
    if (
      !articleId ||
      !window.confirm("سيتم إنشاء مسودة اجتماعية من عنوان وصورة هذا الخبر. لن يتم النشر تلقائيًا.")
    )
      return;
    setActionBusy(`${articleId}:social`);
    setError("");
    try {
      const sourceUrl = String(item.sourceUrl || "").trim();
      const appUrl = `https://msafer.dallne.com/news/${encodeURIComponent(articleId)}`;
      const caption = [
        item.title,
        item.summary,
        `للتفاصيل في تطبيق يا مسافر:\n${appUrl}`,
        sourceUrl ? `المصدر: ${sourceUrl}` : "",
      ]
        .filter(Boolean)
        .join("\n\n");
      const platforms = item.coverImage ? ["facebook", "instagram"] : ["facebook"];
      const post = await adminRequest("/admin/social", {
        method: "POST",
        body: JSON.stringify({
          caption,
          mediaUrl: item.coverImage || "",
          platforms,
          postType: "feed",
        }),
      });
      window.location.assign(`/social?postId=${encodeURIComponent(post.postId)}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setActionBusy("");
    }
  };

  const fetchNewsNow = async () => {
    if (!window.confirm("هل تريد جلب أحدث الأخبار الرسمية وإنشاء مسودات للمراجعة؟")) return;
    setFetchingNews(true);
    setError("");
    setSuccess("");
    try {
      const queued = await resourceAction("articles", "automation", "fetch", {});
      const requestedAt = new Date(queued.requestedAt || Date.now()).getTime();
      setSuccess("بدأ جلب الأخبار. قد تستغرق العملية نحو دقيقة...");

      for (let attempt = 0; attempt < 40; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const run = await getNewsAutomationStatus();
        const startedAt = new Date(run.startedAt || 0).getTime();
        if (!Number.isFinite(startedAt) || startedAt < requestedAt - 2000) continue;

        if (run.status === "running") {
          setSuccess("جارٍ فحص المصادر وإنشاء المسودات...");
          continue;
        }
        if (run.status === "failed") {
          throw new Error("تعذر إكمال جلب الأخبار. حاول مرة أخرى أو راجع سجل التشغيل.");
        }
        if (run.status === "completed") {
          const draftsCreated = Number(run.draftsCreated || 0);
          if (draftsCreated > 0) setSelectedTab("draft");
          load();
          setSuccess(
            draftsCreated > 0
              ? `اكتمل الجلب وتم إنشاء ${draftsCreated} مسودة جديدة للمراجعة.`
              : "اكتمل الجلب ولم يتم العثور على أخبار جديدة غير مكررة."
          );
          return;
        }
      }

      load();
      setSuccess("لا يزال الجلب يعمل في الخلفية. حدّث الصفحة بعد قليل لرؤية النتيجة.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setFetchingNews(false);
    }
  };

  const move = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= orderedItems.length) return;
    const current = orderedItems[index];
    const target = orderedItems[targetIndex];
    try {
      await Promise.all([
        updateResource("articles", current.entityId, {
          sortOrder: Number(target.sortOrder ?? targetIndex),
          expectedVersion: current.version,
        }),
        updateResource("articles", target.entityId, {
          sortOrder: Number(current.sortOrder ?? index),
          expectedVersion: target.version,
        }),
      ]);
      load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const removeArticle = async (item) => {
    const articleId = item.entityId || item.articleId;
    if (
      !articleId ||
      !window.confirm(
        item.status === "published"
          ? "هل تريد حذف هذا الخبر نهائياً؟ سيختفي فوراً من التطبيق ولا يمكن التراجع عن الحذف."
          : "هل تريد حذف هذا الخبر نهائياً؟ لا يمكن التراجع عن الحذف."
      )
    ) {
      return;
    }
    setActionBusy(`${articleId}:delete`);
    setError("");
    setSuccess("");
    try {
      await deleteResource("articles", articleId);
      setSuccess("تم حذف الخبر نهائياً.");
      load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setActionBusy("");
    }
  };

  const uploadCover = async (event) => {
    const file = event.target.files?.[0];
    const articleId = editing?.entityId || editing?.articleId;
    if (!file || !articleId) return;
    setUploading(true);
    setUploadInfo("");
    try {
      const media = await uploadMedia({ file, entityType: "articles", entityId: articleId });
      setForm((current) => ({ ...current, coverImage: media.publicUrl }));
      setUploadInfo(media.optimization?.summary || "تم رفع الصورة بنجاح.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              آخر الأخبار
            </MDTypography>
            <MDTypography variant="button" color="text">
              إدارة المقالات التي تظهر في تطبيق يا مسافر وجدولة نشرها.
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {auth.canPublish && (
              <Button
                variant="outlined"
                startIcon={
                  fetchingNews ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <Icon>auto_awesome</Icon>
                  )
                }
                onClick={fetchNewsNow}
                disabled={fetchingNews}
                sx={{
                  color: "#087A67",
                  borderColor: "#18A88B",
                  fontWeight: 700,
                  "&:hover": {
                    color: "#055F51",
                    borderColor: "#087A67",
                    backgroundColor: "rgba(24, 168, 139, 0.08)",
                  },
                  "&.Mui-disabled": {
                    color: "#5F7D77",
                    borderColor: "#A9C9C2",
                  },
                }}
              >
                {fetchingNews ? "جارٍ جلب الأخبار..." : "جلب الأخبار الآن"}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Icon>add</Icon>}
              onClick={() => openEditor()}
              sx={{
                bgcolor: "#179B82",
                color: "#FFFFFF",
                fontWeight: 700,
                "&:hover": { bgcolor: "#12806B", color: "#FFFFFF" },
              }}
            >
              إضافة مقال
            </Button>
          </MDBox>
        </MDBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <MDBox bgColor="white" borderRadius="xl" shadow="sm" p={2.5}>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="ابحث بعنوان المقال أو وصفه أو تصنيفه"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Tabs
                value={selectedTab}
                onChange={(_event, value) => setSelectedTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="حالة الأخبار"
                sx={{
                  borderBottom: "1px solid #DCE8EB",
                  "& .MuiTab-root": { fontWeight: 700, minHeight: 48 },
                  "& .Mui-selected": { color: "#087A67 !important" },
                  "& .MuiTabs-indicator": { backgroundColor: "#18A88B" },
                }}
              >
                <Tab value="live" label={`الأخبار المنشورة (${tabCounts.live})`} />
                <Tab value="draft" label={`المسودات (${tabCounts.draft})`} />
                <Tab value="archived" label={`المؤرشفة (${tabCounts.archived})`} />
              </Tabs>
            </Grid>
          </Grid>

          {loading ? (
            <MDBox py={8} textAlign="center">
              <CircularProgress color="success" />
            </MDBox>
          ) : orderedItems.length === 0 ? (
            <MDBox py={8} textAlign="center">
              <Icon sx={{ fontSize: 54, color: "#9CB0B8" }}>auto_awesome</Icon>
              <MDTypography color="text">
                {
                  {
                    live: "لا توجد أخبار منشورة مطابقة.",
                    draft: "لا توجد مسودات مطابقة.",
                    archived: "لا توجد أخبار مؤرشفة مطابقة.",
                  }[selectedTab]
                }
              </MDTypography>
            </MDBox>
          ) : (
            orderedItems.map((item, index) => {
              const nextStep = nextWorkflowAction(item, auth.canPublish);
              return (
                <MDBox
                  key={item.entityId}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  py={1.5}
                  borderBottom="1px solid #E1ECEF"
                >
                  <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <MDBox
                      component="img"
                      src={item.coverImage || "/apple-icon.png"}
                      alt=""
                      width="5.5rem"
                      height="4rem"
                      borderRadius="lg"
                      sx={{ objectFit: "cover", bgcolor: "#EEF7F5" }}
                    />
                    <MDBox minWidth={0}>
                      <MDTypography variant="button" fontWeight="bold" display="block">
                        {item.title || "مقال جديد"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {item.category || "دون تصنيف"} · ترتيب {item.sortOrder ?? "—"}
                      </MDTypography>
                      {item.automation && (
                        <MDBox display="flex" gap={1.5} flexWrap="wrap" mt={0.5}>
                          <MDTypography variant="caption" color="text">
                            تاريخ الخبر: {formatArticleDate(item.automation.sourcePublishedAt)}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            تاريخ الجلب:{" "}
                            {formatArticleDate(
                              item.automation.fetchedAt || item.automation.createdAt
                            )}
                          </MDTypography>
                        </MDBox>
                      )}
                      <ArticleWorkflowSteps item={item} />
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    {item.isUrgent && <Chip size="small" label="عاجل" color="error" />}
                    {item.automation && (
                      <Chip size="small" label="مسودة آلية" color="info" variant="outlined" />
                    )}
                    {item.showUnderServices && (
                      <Chip size="small" label="تحت خدماتنا" color="success" />
                    )}
                    <Chip
                      size="small"
                      label={impactLevelMeta(item.impactLevel)[1]}
                      color={impactLevelMeta(item.impactLevel)[2]}
                      variant="outlined"
                    />
                    <Chip size="small" label={statusLabel(item)} color={statusColor(item)} />
                    <Tooltip title="تحريك للأعلى">
                      <span>
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          <Icon>arrow_upward</Icon>
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="تحريك للأسفل">
                      <span>
                        <IconButton
                          size="small"
                          disabled={index === orderedItems.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          <Icon>arrow_downward</Icon>
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={articleActionButtonSx.neutral}
                      onClick={() => openEditor(item)}
                    >
                      تعديل
                    </Button>
                    {auth.canPublish && item.status === "published" && (
                      <Button
                        size="small"
                        variant="contained"
                        sx={articleActionButtonSx.emphasis}
                        disabled={actionBusy === `${item.entityId}:social`}
                        onClick={() => sendToSocial(item)}
                      >
                        {actionBusy === `${item.entityId}:social`
                          ? "جارٍ تجهيز المنشور..."
                          : "نشر اجتماعي"}
                      </Button>
                    )}
                    {nextStep && (
                      <Button
                        size="small"
                        variant="contained"
                        sx={articleActionButtonSx.primary}
                        disabled={
                          nextStep.disabled || actionBusy === `${item.entityId}:${nextStep.action}`
                        }
                        onClick={() => nextStep.action && action(item, nextStep.action)}
                      >
                        {actionBusy === `${item.entityId}:${nextStep.action}`
                          ? "جارٍ تنفيذ الخطوة..."
                          : nextStep.label}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={articleActionButtonSx.neutral}
                      onClick={() => action(item, "duplicate")}
                    >
                      نسخ
                    </Button>
                    {auth.canPublish &&
                      item.status === "published" &&
                      ["failed", "no_recipients"].includes(item.notificationStatus) && (
                        <Button
                          size="small"
                          variant="contained"
                          sx={articleActionButtonSx.emphasis}
                          onClick={() => action(item, "notification")}
                        >
                          إعادة محاولة الإشعار
                        </Button>
                      )}
                    {item.notificationStatus === "no_recipients" && (
                      <Chip size="small" color="warning" label="لم يُرسل: لا يوجد مستلمون مؤهلون" />
                    )}
                    {item.notificationStatus === "sent" && (
                      <Chip size="small" color="success" label="تم إرسال الإشعار" />
                    )}
                    {auth.canPublish &&
                      item.status === "published" &&
                      (Number(item.followersNotificationVersion ?? -1) ===
                      Number(item.publishedVersion ?? 0) ? (
                        <Chip size="small" color="info" label="تم إشعار المتابعين" />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          sx={articleActionButtonSx.emphasis}
                          onClick={() => action(item, "followers-notification")}
                        >
                          إرسال تحديث للمتابعين
                        </Button>
                      ))}
                    {auth.canPublish && item.status !== "archived" && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={articleActionButtonSx.danger}
                        onClick={() => action(item, "archive")}
                      >
                        أرشفة
                      </Button>
                    )}
                    {auth.canPublish && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={articleActionButtonSx.danger}
                        disabled={actionBusy === `${item.entityId}:delete`}
                        onClick={() => removeArticle(item)}
                      >
                        {actionBusy === `${item.entityId}:delete` ? "جارٍ الحذف..." : "حذف نهائي"}
                      </Button>
                    )}
                  </MDBox>
                </MDBox>
              );
            })
          )}
        </MDBox>
      </MDBox>

      <ArticleEditor
        open={editing !== null}
        articleId={editing?.entityId || editing?.articleId}
        form={form}
        automation={editing?.automation}
        setForm={setForm}
        saving={saving}
        uploading={uploading}
        uploadInfo={uploadInfo}
        onUploadCover={uploadCover}
        onPreview={() => setPreviewOpen(true)}
        onClose={closeEditor}
        onSave={save}
      />
      <MobilePreview open={previewOpen} article={form} onClose={() => setPreviewOpen(false)} />
    </DashboardLayout>
  );
}

function ArticleEditor({
  open,
  articleId,
  form,
  automation,
  setForm,
  saving,
  uploading,
  uploadInfo,
  onUploadCover,
  onPreview,
  onClose,
  onSave,
}) {
  const change = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir="rtl">
      <DialogTitle>{articleId ? "تحرير المقال" : "إضافة مقال جديد"}</DialogTitle>
      <DialogContent dividers>
        {!articleId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            احفظ المسودة أولاً ليصبح رفع صورة الغلاف متاحاً.
          </Alert>
        )}
        {automation && (
          <Alert severity="info" sx={{ mb: 2 }}>
            هذه مسودة آلية وتحتاج مراجعة بشرية كاملة قبل الاعتماد.
            <MDBox mt={0.5}>
              <MDTypography variant="caption" display="block">
                تاريخ الخبر: {formatArticleDate(automation.sourcePublishedAt)}
              </MDTypography>
              <MDTypography variant="caption" display="block">
                تاريخ الجلب: {formatArticleDate(automation.fetchedAt || automation.createdAt)}
              </MDTypography>
            </MDBox>
            {automation.sourceUrl && (
              <Button
                component="a"
                href={automation.sourceUrl}
                target="_blank"
                rel="noreferrer"
                size="small"
                sx={{ mr: 1 }}
              >
                فتح المادة الرسمية للمراجعة
              </Button>
            )}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="العنوان"
              value={form.title}
              onChange={change("title")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>التصنيف</InputLabel>
              <Select value={form.category} label="التصنيف" onChange={change("category")}>
                {categories.map((category) => (
                  <MenuItem value={category} key={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="الموقع المرتبط بالخبر"
              value={form.locationLabel}
              placeholder="مثال: مطار بن غوريون"
              onChange={change("locationLabel")}
            />
          </Grid>
          <Grid item xs={12} md={4} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  color="error"
                  checked={Boolean(form.isUrgent)}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, isUrgent: event.target.checked }))
                  }
                />
              }
              label="خبر عاجل"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth required>
              <InputLabel>مدى تأثير الخبر</InputLabel>
              <Select
                value={form.impactLevel}
                label="مدى تأثير الخبر"
                onChange={change("impactLevel")}
              >
                {impactLevels.map(([id, label]) => (
                  <MenuItem value={id} key={id}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              label="الوصف المختصر"
              value={form.summary}
              inputProps={{ maxLength: 280 }}
              helperText={`${form.summary.length}/280`}
              onChange={change("summary")}
            />
          </Grid>
          <Grid item xs={12}>
            <RichTextEditor
              value={form.contentHtml}
              onChange={(contentHtml) => setForm((current) => ({ ...current, contentHtml }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="موعد النشر"
              value={toLocalDateTime(form.publishAt)}
              InputLabelProps={{ shrink: true }}
              helperText="اتركه فارغاً للنشر فور اعتماد المقال."
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  publishAt: fromLocalDateTime(event.target.value),
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="الترتيب اليدوي"
              value={form.sortOrder}
              onChange={change("sortOrder")}
            />
          </Grid>
          <Grid item xs={12} md={3} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  color="success"
                  checked={Boolean(form.showUnderServices)}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      showUnderServices: event.target.checked,
                    }))
                  }
                />
              }
              label="إظهار تحت خدماتنا"
            />
          </Grid>
          <Grid item xs={12}>
            <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Button component="label" variant="outlined" disabled={!articleId || uploading}>
                {uploading ? "جارٍ رفع الصورة..." : "رفع صورة الغلاف"}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={onUploadCover}
                />
              </Button>
              <TextField
                sx={{ flex: 1, minWidth: 260 }}
                label="رابط صورة الغلاف"
                value={form.coverImage}
                onChange={change("coverImage")}
              />
            </MDBox>
            {uploadInfo && (
              <MDTypography variant="caption" color="success" display="block" mt={1}>
                {uploadInfo}
              </MDTypography>
            )}
            {form.coverImage && (
              <MDBox
                component="img"
                src={form.coverImage}
                alt="معاينة صورة الغلاف"
                mt={1.5}
                width="100%"
                height="13rem"
                borderRadius="xl"
                sx={{ objectFit: "cover" }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button onClick={onPreview} startIcon={<Icon>phone_iphone</Icon>}>
          معاينة الهاتف
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving}
          sx={{ bgcolor: "#179B82", "&:hover": { bgcolor: "#12806B" } }}
        >
          {saving ? "جارٍ الحفظ..." : "حفظ كمسودة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const command = (name, argument = null) => {
    editorRef.current?.focus();
    document.execCommand(name, false, argument);
    onChange(editorRef.current?.innerHTML || "");
  };

  const addLink = () => {
    const href = window.prompt("ألصق رابطاً يبدأ بـ https://");
    if (href?.startsWith("https://")) command("createLink", href);
  };

  return (
    <MDBox>
      <MDTypography variant="button" fontWeight="bold" display="block" mb={0.75}>
        محتوى المقال المنسّق *
      </MDTypography>
      <MDBox display="flex" gap={0.5} flexWrap="wrap" mb={1}>
        <Button size="small" variant="outlined" onClick={() => command("formatBlock", "h2")}>
          عنوان
        </Button>
        <Button size="small" variant="outlined" onClick={() => command("formatBlock", "p")}>
          فقرة
        </Button>
        <IconButton size="small" onClick={() => command("bold")}>
          <Icon>format_bold</Icon>
        </IconButton>
        <IconButton size="small" onClick={() => command("insertUnorderedList")}>
          <Icon>format_list_bulleted</Icon>
        </IconButton>
        <IconButton size="small" onClick={addLink}>
          <Icon>link</Icon>
        </IconButton>
        <IconButton size="small" onClick={() => command("removeFormat")}>
          <Icon>format_clear</Icon>
        </IconButton>
      </MDBox>
      <MDBox
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(event) => onChange(event.currentTarget.innerHTML)}
        minHeight="15rem"
        p={2}
        border="1px solid #C7D4D8"
        borderRadius="lg"
        sx={{ outline: "none", "&:focus": { borderColor: "#179B82" } }}
      />
    </MDBox>
  );
}

function MobilePreview({ open, article, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle>معاينة الهاتف</DialogTitle>
      <DialogContent
        dividers
        sx={{ bgcolor: "#F2FAFC", display: "flex", justifyContent: "center" }}
      >
        <MDBox
          width="23rem"
          maxWidth="100%"
          bgcolor="white"
          borderRadius="2rem"
          overflow="hidden"
          shadow="lg"
        >
          <MDBox
            component="img"
            src={article.coverImage || "/apple-icon.png"}
            alt=""
            width="100%"
            height="13rem"
            sx={{ objectFit: "cover" }}
          />
          <MDBox p={2.5}>
            {article.isUrgent && <Chip size="small" label="⚡ عاجل" color="error" sx={{ ml: 1 }} />}
            <Chip size="small" label={article.category || "أخبار السفر"} color="success" />
            <Chip
              size="small"
              label={impactLevelMeta(article.impactLevel)[1]}
              color={impactLevelMeta(article.impactLevel)[2]}
              variant="outlined"
              sx={{ mr: 1 }}
            />
            {article.locationLabel && (
              <MDTypography variant="caption" color="text" display="block" mt={1.25}>
                <Icon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }}>location_on</Icon>
                {article.locationLabel}
              </MDTypography>
            )}
            <MDTypography variant="h4" fontWeight="bold" mt={1.5}>
              {article.title || "عنوان المقال"}
            </MDTypography>
            <MDTypography variant="button" color="text" display="block" mt={1}>
              {article.summary || "الوصف المختصر للمقال يظهر هنا."}
            </MDTypography>
            <MDBox
              mt={2}
              p={1.5}
              border="1px solid #DCE9EC"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <MDBox>
                <MDTypography variant="button" fontWeight="bold" display="block">
                  تابع هذا الخبر
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  سنخبرك بأي تغيير مهم
                </MDTypography>
              </MDBox>
              <Icon color="success">notifications_none</Icon>
            </MDBox>
            <MDBox
              mt={2}
              sx={{ lineHeight: 1.9, color: "#173946" }}
              dangerouslySetInnerHTML={{ __html: safePreviewHtml(article.contentHtml) }}
            />
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
}

ArticleEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  articleId: PropTypes.string,
  form: PropTypes.object.isRequired,
  automation: PropTypes.object,
  setForm: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  uploading: PropTypes.bool.isRequired,
  uploadInfo: PropTypes.string.isRequired,
  onUploadCover: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
ArticleEditor.defaultProps = { articleId: null, automation: null };
RichTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
MobilePreview.propTypes = {
  open: PropTypes.bool.isRequired,
  article: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ArticlesPage;
